import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";

import { createNoIndexMeta } from "~/lib/shared/meta";

const paramsSchema = z.object({ offerId: z.string().uuid() });

// Server fn (not a plain call from the loader): the loader runs in the
// browser on client-side navigation, and Stripe session creation must only
// ever execute server-side.
export const createSummitSpecialCheckout = createServerFn({ method: "POST" })
  .validator(paramsSchema)
  .handler(async ({ data }) => {
    const { createOfferCheckoutSession } =
      await import("~/lib/server/checkout/offer-checkout-session");
    const { offerId } = data;
    const baseUrl = process.env.BASE_URL || "https://dreamcoachmatch.com";

    try {
      const session = await createOfferCheckoutSession({
        offerId,
        successUrl: `${baseUrl}/summit-special/thank-you?offerId=${offerId}`,
        cancelUrl: `${baseUrl}/summit-special/thank-you?offerId=${offerId}&status=cancelled`,
        includeSessionIdParam: true,
      });

      return {
        checkoutUrl: session.checkoutUrl,
        offerTitle: session.offerTitle,
      };
    } catch (error) {
      if (
        error instanceof Error &&
        (error.message === "Offer not found" ||
          error.message === "Coach not found")
      ) {
        throw notFound();
      }
      throw error;
    }
  });

export const Route = createFileRoute("/summit-special/checkout/$offerId")({
  head: () =>
    createNoIndexMeta(
      "Summit Special Checkout",
      "Processing your summit special checkout.",
    ),
  loader: ({ params }) =>
    createSummitSpecialCheckout({ data: paramsSchema.parse(params) }),
  component: CheckoutRedirectPage,
});

function CheckoutRedirectPage() {
  const { checkoutUrl, offerTitle } = Route.useLoaderData();

  useEffect(() => {
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
    }
  }, [checkoutUrl]);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-4 text-center">
      <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
      <div>
        <h1 className="text-xl font-semibold text-foreground">
          Redirecting to secure checkout
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {offerTitle ?? "Summit special"} is loading in Stripe. This will only
          take a moment.
        </p>
      </div>
    </div>
  );
}
